import type { CodeDef } from '@/shared/helpers/dataHelpers';

/**
 * Translates AG Grid's filter model into the CLQL filter format used by the API for
 * export, publish and lookup URLs.
 *
 * The translation has to be exact: publish acts on the codes the query matches, so a filter
 * that does not translate faithfully would publish the wrong codes. The grid is therefore
 * configured (see `getFilterOptions`) to only offer the operators that can be expressed here.
 */

export type ClqlQuery = string[];

export type TextFilterCondition = {
    filterType: 'text';
    type: string;
    filter?: string | null;
};

export type NumberFilterCondition = {
    filterType: 'number';
    type: string;
    filter?: number | null;
    filterTo?: number | null;
};

export type DateFilterCondition = {
    filterType: 'date';
    type: string;
    dateFrom?: string | null;
    dateTo?: string | null;
};

export type SetFilterModel = {
    filterType: 'set';
    values: (string | null)[];
};

export type SimpleFilterModel = (TextFilterCondition | NumberFilterCondition | DateFilterCondition) & {
    operator?: 'AND' | 'OR';
    conditions?: (TextFilterCondition | NumberFilterCondition | DateFilterCondition)[];
};

export type ColumnFilterModel = SimpleFilterModel | SetFilterModel;

export type FilterModel = Record<string, ColumnFilterModel>;

// Operators that the API understands. `is`/`is not` only ever take the operand `null`.
const OP_EQUAL = '=';
const OP_NOT_EQUAL = '!=';
const OP_GREATER_THAN = '>';
const OP_GREATER_OR_EQUAL = '>=';
const OP_LESS_THAN = '<';
const OP_LESS_OR_EQUAL = '<=';
const OP_LIKE = 'like';
const OP_IN = 'in';
// Selecting values together with the blanks needs an OR, which a plain condition cannot express,
// so the API is asked for the combination directly.
const OP_IN_OR_NULL = 'in or null';
const OP_IS = 'is';
const OP_IS_NOT = 'is not';

// The last day SQL Server's date types can hold. The day after it cannot be sent as a bound, and
// every value already lies before its end, so no upper bound is needed for it.
const LAST_DAY = '9999-12-31';

const numberFilterOptions = ['equals', 'notEqual', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual', 'inRange', 'blank', 'notBlank'];
// `notEqual` is left out because "not on this day" needs an OR of two ranges.
const dateFilterOptions = ['equals', 'greaterThan', 'lessThan', 'inRange', 'blank', 'notBlank'];

/**
 * Only valid codes are shown until the user says otherwise, matching the default the
 * previous query bar applied.
 */
export const defaultFilterModel: FilterModel = {
    isValid: { filterType: 'set', values: ['true'] }
};

/**
 * The filter operators offered for a column, limited to those that translate to CLQL.
 *
 * There is deliberately no entry for `agTextColumnFilter`: no column asks for one. Text columns
 * are given a set filter instead, because reference data repeats and a pick list of the distinct
 * values is more useful than typing a fragment. See `isConditionTypeAllowed` for what enabling one
 * would require.
 */
export function getFilterOptions(filter: string | boolean): string[] | undefined {
    switch (filter) {
        case 'agNumberColumnFilter': return numberFilterOptions;
        case 'agDateColumnFilter': return dateFilterOptions;
        default: return undefined;
    }
}

/**
 * Whether a condition may be used with a kind of filter. A hand written link could otherwise ask
 * for an operator the column never offers, such as "not on this day", which the grid would apply
 * but the translation cannot express, leaving export and publish acting on the hidden rows.
 *
 * Text conditions are refused outright. No column is given a text filter, so the only way to reach
 * one is a hand written link naming a column the grid cannot filter at all, such as an SVG column.
 * The grid would show every row while export and publish acted on the `like` the condition
 * translates to, which is the disagreement this function exists to prevent.
 *
 * Two things need fixing before a text filter could be offered, both of which would otherwise let
 * export and publish act on rows the grid does not show:
 *
 * - `%`, `_` and `[` are wildcards to SQL's `LIKE`. The query spec path does not escape them, so a
 *   value containing one matches more rows than the grid does. `SqlExtensions.LikeContainsExpression`
 *   shows the escaping the server would need.
 * - `notEqual` keeps the rows with no value at all in the grid, while SQL's `!=` drops them. It
 *   would need the `is null` arm that the set filter already gets through `in or null`.
 */
export function isConditionTypeAllowed(kind: 'set' | 'text' | 'number' | 'date', type: string): boolean {
    switch (kind) {
        case 'number': return numberFilterOptions.includes(type);
        case 'date': return dateFilterOptions.includes(type);
        default: return false;
    }
}

/**
 * Whether a column's set filter tells values apart by case. The database compares `Identity` with
 * a binary collation, so `AB` and `ab` are two identities there and the grid must list them
 * separately. `CommonLibraryIRI` ends in a Base62 encoding, whose letters are case-sensitive, and
 * the API looks it up exactly. Every other column is compared without regard to case, as the set
 * filter does by default.
 */
export function isCaseSensitiveColumn(field: string): boolean {
    return field === 'identity' || field === 'commonLibraryIRI';
}

/**
 * Whether a set filter on the column may be sent as the values left out, or as "has a value" when
 * none are, rather than as the values selected. Both read the column's values as they were when the
 * page loaded, so they only stand for the selection while the code set has not changed since.
 *
 * A yes/no column is never shortened: listing its selection is at most two values anyway, and the
 * default filter selects only valid codes. Were every loaded code valid, that would otherwise be
 * sent as "has a value", and a code made invalid after the page loaded would then be exported or
 * published along with the valid ones.
 */
function canShortenToExcluded(column: CodeDef): boolean {
    return column.type.toUpperCase() !== 'BOOL';
}

/**
 * Why export, publish and copy cannot run on these queries, or `null` when they can.
 *
 * The API does not compare `CommonLibraryIRI` as text: it parses the IRI and looks the item up,
 * and it can only do that for one IRI (`IriConditionOptimizer` on the server). Any other condition
 * on it, which the set filter produces as soon as more than one IRI is selected, a few are left
 * out or the blanks are picked, makes the API fail rather than filter.
 */
export function getUnsupportedQueryMessage(queries: ClqlQuery[]): string | null {
    const unsupportedIri = queries.some(([field, operator]) => field.toLowerCase() === 'commonlibraryiri' && operator !== OP_EQUAL);
    if (unsupportedIri)
        return 'Export, publish and copy can only filter CommonLibraryIRI on a single value, as the server looks the item up by its IRI. Select one IRI in that column\'s filter, or clear it.';

    return null;
}

export function toClqlQueries(filterModel: FilterModel | null, columns: CodeDef[], distinctValues?: Map<string, string[]>): ClqlQuery[] {
    if (!filterModel)
        return [];

    const queries: ClqlQuery[] = [];

    for (const [field, model] of Object.entries(filterModel)) {
        const column = columns.find(c => c.field === field);
        if (!column)
            continue;

        queries.push(...translateColumnFilter(field, model, canShortenToExcluded(column) ? distinctValues?.get(field) : undefined));
    }

    return queries;
}

function translateColumnFilter(field: string, model: ColumnFilterModel, allValues?: string[]): ClqlQuery[] {
    if (model.filterType === 'set')
        return translateSetFilter(field, model, isCaseSensitiveColumn(field), allValues);

    // A column can hold two conditions. Only AND is offered, because the API combines all
    // queries with AND and cannot express an OR between them.
    if (model.conditions?.length)
        return model.conditions.flatMap(condition => translateCondition(field, condition));

    return translateCondition(field, model);
}

function translateSetFilter(field: string, model: SetFilterModel, caseSensitive: boolean, allValues?: string[]): ClqlQuery[] {
    const values = model.values ?? [];
    const blanksSelected = values.some(value => value === null || value === '');
    const selected = values.filter((value): value is string => value !== null && value !== '');

    if (selected.length === 0) {
        if (blanksSelected)
            return [[field, OP_IS, 'null']];

        // Nothing is selected, so the grid shows no rows at all. Sending no condition would mean
        // "no filter" to the API, so a contradiction is sent instead to match nothing.
        return matchNothing(field);
    }

    if (blanksSelected)
        return [[field, OP_IN_OR_NULL, ...selected]];

    // Deselecting a few values out of many would otherwise produce a huge IN list, which risks
    // running into the database's parameter limit. The excluded values say the same thing, and
    // because blanks are not selected here the rows without a value are excluded either way.
    if (allValues && allValues.length > 0) {
        // A value differing from a selected one only in case is shown by the grid unless the
        // column tells case apart, and the database compares the same way, so it must not be
        // excluded. The set filter folds case with `toUpperCase`.
        const toKey = caseSensitive ? (value: string) => value : (value: string) => value.toUpperCase();
        const selectedKeys = new Set(selected.map(toKey));
        const excluded = allValues.filter(value => !selectedKeys.has(toKey(value)));

        // Every value is selected but the blanks are not, so the filter means "has a value".
        // Sending no condition would let export and publish keep the blank rows that the grid
        // is hiding.
        if (excluded.length === 0)
            return [[field, OP_IS_NOT, 'null']];

        if (excluded.length < selected.length)
            return excluded.map(value => [field, OP_NOT_EQUAL, value]);
    }

    if (selected.length === 1)
        return [[field, OP_EQUAL, selected[0]]];

    return [[field, OP_IN, ...selected]];
}

/** A pair of conditions that no row can satisfy. */
function matchNothing(field: string): ClqlQuery[] {
    return [[field, OP_IS, 'null'], [field, OP_IS_NOT, 'null']];
}

function translateCondition(field: string, condition: TextFilterCondition | NumberFilterCondition | DateFilterCondition): ClqlQuery[] {
    if (condition.type === 'blank')
        return [[field, OP_IS, 'null']];
    if (condition.type === 'notBlank')
        return [[field, OP_IS_NOT, 'null']];

    if (condition.filterType === 'date')
        return translateDateCondition(field, condition);

    if (condition.type === 'inRange') {
        const [from, to] = getRange(condition);
        if (from === null || to === null)
            return [];

        // The grid is configured with `inRangeInclusive`, as AG Grid's own default leaves out both ends.
        return [[field, OP_GREATER_OR_EQUAL, from], [field, OP_LESS_OR_EQUAL, to]];
    }

    const operator = getOperator(condition.type);
    const value = getValue(condition);
    if (!operator || value === null)
        return [];

    return [[field, operator, value]];
}

/**
 * Date columns hold a time as well, while the filter works on whole days. The conditions are
 * therefore expressed as day boundaries so that the API matches the same rows as the grid.
 */
function translateDateCondition(field: string, condition: DateFilterCondition): ClqlQuery[] {
    const from = toDateValue(condition.dateFrom);
    if (from === null)
        return [];

    switch (condition.type) {
        case 'equals':
            return [[field, OP_GREATER_OR_EQUAL, from], ...beforeEndOf(field, from)];
        case 'greaterThan':
            // Nothing comes after the last day a date column can hold.
            return from === LAST_DAY ? matchNothing(field) : [[field, OP_GREATER_OR_EQUAL, nextDay(from)]];
        case 'lessThan':
            return [[field, OP_LESS_THAN, from]];
        case 'inRange': {
            const to = toDateValue(condition.dateTo);
            if (to === null)
                return [];

            return [[field, OP_GREATER_OR_EQUAL, from], ...beforeEndOf(field, to)];
        }
        default:
            return [];
    }
}

function beforeEndOf(field: string, day: string): ClqlQuery[] {
    return day === LAST_DAY ? [] : [[field, OP_LESS_THAN, nextDay(day)]];
}

// Only ever called with a day that `toDateValue` has already accepted, and never with the last
// day, so the date it builds is valid and `toISOString` cannot throw or go past year 9999.
function nextDay(date: string): string {
    const [year, month, day] = date.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    return next.toISOString().split('T')[0];
}

function getOperator(type: string): string | null {
    switch (type) {
        case 'contains': return OP_LIKE;
        case 'equals': return OP_EQUAL;
        case 'notEqual': return OP_NOT_EQUAL;
        case 'greaterThan': return OP_GREATER_THAN;
        case 'greaterThanOrEqual': return OP_GREATER_OR_EQUAL;
        case 'lessThan': return OP_LESS_THAN;
        case 'lessThanOrEqual': return OP_LESS_OR_EQUAL;
        default: return null;
    }
}

function getValue(condition: TextFilterCondition | NumberFilterCondition | DateFilterCondition): string | null {
    if (condition.filterType === 'date')
        return toDateValue(condition.dateFrom);

    const value = condition.filter;
    if (value === null || value === undefined || value === '')
        return null;

    return String(value);
}

function getRange(condition: TextFilterCondition | NumberFilterCondition | DateFilterCondition): [string | null, string | null] {
    if (condition.filterType === 'date')
        return [toDateValue(condition.dateFrom), toDateValue(condition.dateTo)];

    if (condition.filterType === 'number')
        return [toScalar(condition.filter), toScalar(condition.filterTo)];

    return [null, null];
}

function toScalar(value?: number | null): string | null {
    return value === null || value === undefined ? null : String(value);
}

// AG Grid serialises dates as 'YYYY-MM-DD HH:mm:ss'; the API expects a date only.
/**
 * The day part of a stored date, or `null` if the value is not a real calendar day.
 *
 * Validating here keeps every date condition total: a filter model can carry anything, because it
 * is restored from browser storage as well as read from a link, and a value that is not a date used
 * to reach `nextDay` and throw. That throw escaped `toClqlQueries` and left the page believing
 * there was no filter at all, which the API reads as "everything" — so export and publish quietly
 * acted on the whole code set while the grid showed a subset.
 */
function toDateValue(value?: string | null): string | null {
    if (!value)
        return null;

    const day = value.split(' ')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day))
        return null;

    const [year, month, date] = day.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, date));

    const isRealDay = parsed.getUTCFullYear() === year
        && parsed.getUTCMonth() === month - 1
        && parsed.getUTCDate() === date;

    return isRealDay ? day : null;
}
